/**
 * 🚨 CRITICAL COMPONENT - CORE STICKER CARD
 *
 * Universal sticker card used in modals for purchasing. Contains image optimization.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/MODAL_IMAGE_PRIORITY_SYSTEM.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Read /docs/ESSENTIAL/RECENT_OPTIMIZATIONS.md
 *
 * 🔒 CRITICAL RULES:
 * - NEVER change optimizeImageUrl() to use il_fullxfull (use il_570xN)
 * - NEVER remove onLoad/onError handlers from img element
 * - ALWAYS use isPriorityLoad prop when in modal
 * - NEVER bulk preload variation images (causes 35-second delays)
 *
 * 📍 CRITICAL SECTIONS:
 * - Lines 235-241: optimizeImageUrl() helper (il_570xN conversion)
 * - Lines 476-479: Priority loading attributes on <img>
 * - Lines 316-327: Image loading state management
 *
 * ⚡ PERFORMANCE:
 * - il_570xN images: ~200KB (10-25x faster than il_fullxfull)
 * - Modal load time: < 2 seconds (was 30+ seconds)
 *
 * Last reviewed: 2025-01-15
 */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { COLORS, getColorNames } from '../data/colors';
import { getLargestAvailableSize } from '../data/constants';
import databaseService from '../services/databaseService';
import MaterialIcon from './MaterialIcon';
import ClothedEyeLoader from './ClothedEyeLoader';
import { ETSY_IMAGE_SIZE_OPTIMIZED, ETSY_IMAGE_SIZE_FULL } from '../config/uiConstants';
// Using exact Etsy variation data; no normalization
import logger from '../utils/logger.js';
import './CoreStickerCard.css';

// Helper function to decode HTML entities in text
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  return text
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ');
};

// Extract image ID from Etsy URL (e.g., "6797659165" from ".../il_fullxfull.6797659165_j6pr.jpg")
const extractImageIdFromUrl = (url) => {
  const match = String(url || '').match(/il_fullxfull\.(\d+)_/);
  return match ? parseInt(match[1], 10) : null;
};

const CoreStickerCard = ({
  listingId,
  title = '',
  description = '',
  etsyUrl = '',
  category = '',
  onAdd,
  globalSize = 'MICRO',
  globalColor = 'Black',
  isAdminMode = false,
  onCardClick = null,
  buttonText = 'BUY IT NOW',
  buttonIcon = 'add',
  renderUI = null,
  onPriceUpdate = null,
  onSizeColorChange = null, // NEW: Callback when size/color changes
  testMode = false, // NEW: Force loading state for testing
  modalMode = false, // NEW: Optimized layout for modal context
  isPriorityLoad = false, // NEW: Priority loading for deep links
  onImageLoad = null // NEW: Callback when image loads (for modal optimization)
}) => {
  // Core state
  const [selectedSize, setSelectedSize] = useState(globalSize);
  const [selectedColor, setSelectedColor] = useState(globalColor);
  const [currentImage, setCurrentImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [forceImageLoading, setForceImageLoading] = useState(false);

  // Data from database
  const [dbData, setDbData] = useState(null);

  // Log priority loading
  if (isPriorityLoad) {
    logger.log(`🎯 PRIORITY MODAL IMAGE LOAD: ${title || listingId}`);
  }
  const [listingData, setListingData] = useState(null);
  const [images, setImages] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [variationImages, setVariationImages] = useState({});
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);

  // Helper function to process unified data (shared by cache and API paths)
  const processUnifiedData = useCallback((unifiedData) => {
    if (!unifiedData) {
      setError('No data found for this listing');
      setLoading(false);
      return;
    }

    // Store unified data (no JSON parsing needed)
    setDbData(unifiedData);

    // Data is already parsed in unified structure
    const parsedListing = {
      title: unifiedData.title,
      description: unifiedData.description,
      state: unifiedData.state,
      views: unifiedData.views,
      num_favorers: unifiedData.num_favorers,
      url: unifiedData.etsyUrl
    };
    // all_image_urls is already parsed by the API - ensure HTTPS and valid shape
    const imageUrlsRaw = Array.isArray(unifiedData.all_image_urls) ? unifiedData.all_image_urls : [];
    const toHttps = (u) => typeof u === 'string' ? u.replace(/^http:\/\//i, 'https://') : u;
    const imageUrls = imageUrlsRaw.map(toHttps).filter(Boolean);
    const parsedImages = { results: imageUrls.map((url, index) => ({ 
      url_fullxfull: url,
      listing_image_id: extractImageIdFromUrl(url) // Extract image ID from URL for variation mapping
    })) };
    // Normalize inventory_matrix (may arrive as parsed object or JSON string)
    let parsedInventory = unifiedData.inventory_matrix || {};
    if (typeof parsedInventory === 'string') {
      try { parsedInventory = JSON.parse(parsedInventory); } catch (_) {}
    }

    // Normalize variation_images_data (may arrive as object with results, array, or JSON string)
    let parsedVariationImages = unifiedData.variation_images_data || { results: [] };
    if (typeof parsedVariationImages === 'string') {
      try { parsedVariationImages = JSON.parse(parsedVariationImages); } catch (_) {}
    }
    const variationResults = Array.isArray(parsedVariationImages?.results)
      ? parsedVariationImages.results
      : (Array.isArray(parsedVariationImages) ? parsedVariationImages : []);
    parsedVariationImages = { results: variationResults };

    setListingData(parsedListing);
    setImages(parsedImages.results || []);
    setInventory(parsedInventory);
    setVariationImages(parsedVariationImages);

    // Build exact option lists in Etsy order (no normalization)
    const sizesInOrder = [];
    const colorsInOrder = [];
    if (parsedInventory?.products) {
      parsedInventory.products.forEach((product) => {
        product.property_values?.forEach((prop) => {
          if (prop.property_name === 'Size' || prop.property_name === 'Style') {
            prop.values?.forEach((value) => {
              if (!sizesInOrder.includes(value)) sizesInOrder.push(value);
            });
          }
          if (prop.property_name === 'Primary color') {
            prop.values?.forEach((value) => {
              if (!colorsInOrder.includes(value)) colorsInOrder.push(value);
            });
          }
        });
      });
    }

    setAvailableSizes(sizesInOrder);
    setAvailableColors(colorsInOrder);

    setLoading(false);

    // Signal that modal is ready so grid can start loading
    if (isPriorityLoad) {
      // Fire event immediately - don't wait for all variations to preload
      window.dispatchEvent(new CustomEvent('modalImagesReady', {
        detail: { timestamp: Date.now() }
      }));
      logger.log('🎉 modalImagesReady event fired (on-demand loading mode)');
    }
  }, [isPriorityLoad]);

  // Load data from database
  const loadData = useCallback(async () => {
    if (!listingId) {
      setError('No listing ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // 🚀 OPTIMIZATION: Skip API call if this is a duplicate request within 5 seconds
      const cacheKey = `listing_${listingId}`;
      const now = Date.now();
      if (!window.__listingLoadCache) window.__listingLoadCache = {};
      const cached = window.__listingLoadCache[cacheKey];
      
      if (cached && (now - cached.timestamp) < 5000) {
        logger.log(`⚡ Using cached data for ${listingId} (avoiding duplicate API call)`);
        // Use cached data
        const unifiedData = cached.data;
        // Skip to processing (continue below)
        processUnifiedData(unifiedData);
        return;
      }
      
      const unifiedData = await databaseService.getEtsyListingData(listingId);
      
      // Cache the result
      window.__listingLoadCache[cacheKey] = {
        data: unifiedData,
        timestamp: now
      };
      
      // Process the data
      processUnifiedData(unifiedData);
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError('Failed to load listing data');

      setLoading(false);
    }
  }, [listingId, isPriorityLoad, processUnifiedData]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update selected size and color after data loads to match global props
  useEffect(() => {
    if (!loading && availableSizes.length > 0) {
      // Check if the globalSize is available in the loaded sizes
      if (availableSizes.includes(globalSize)) {
        setSelectedSize(globalSize);
      } else {
        // 🚨 CRITICAL FIX: Priority order: MICRO first, then LARGEST available 
        // size (not first)
        const microSize = availableSizes.find(size => 
          size.toUpperCase().includes('MICRO')
        );
        
        const preferredSize = microSize || getLargestAvailableSize(availableSizes) || availableSizes[0];
        setSelectedSize(preferredSize);
      }
    }
  }, [loading, availableSizes, globalSize]);

  useEffect(() => {
    if (!loading && availableColors.length > 0) {
      // Check if the globalColor is available in the loaded colors
      if (availableColors.includes(globalColor)) {
        setSelectedColor(globalColor);
      } else {
        setSelectedColor(availableColors[0] || 'Black (Matte)');
      }
    }
  }, [loading, availableColors, globalColor]);

  // Debug functionality - Press keys for debugging
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'L' || e.key === 'l') {
        logger.log('🔄 Forcing image loading state for debugging');
        setForceImageLoading(prev => !prev);
      }
      if (e.key === 'D' || e.key === 'd') {
        logger.log('🔍 DEBUG INFO:');
        logger.log('Selected Size:', selectedSize);
        logger.log('Selected Color:', selectedColor);
        logger.log('Current Image:', currentImage);
        logger.log('All Images:', images);
        logger.log('Variation Images:', variationImages);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedSize, selectedColor, currentImage, images, variationImages]);

  // Helper: Optimize Etsy image URLs for faster loading
  // Uses ETSY_IMAGE_SIZE_OPTIMIZED (il_570xN) instead of il_fullxfull
  // il_570xN is ~200KB vs 2-5MB, loads 10x faster with minimal quality loss
  const optimizeImageUrl = useCallback((url) => {
    if (!url) return null;
    // Use optimized size for all cases - much faster loading
    return url.replace(new RegExp(ETSY_IMAGE_SIZE_FULL, 'g'), ETSY_IMAGE_SIZE_OPTIMIZED);
  }, []);

  // Get current image based on selected size and color
  const getCurrentImage = useCallback(() => {
    if (!images || images.length === 0) return null;

    let imageUrl = null;

    // Use official variation images mapping if available
    if (variationImages.results && variationImages.results.length > 0) {
      const normalizeValue = (v) => String(v || '')
        .toLowerCase()
        .trim();
      const getVarValue = (v) => normalizeValue(
        (v && (v.value ?? v.formatted_value ?? v.value_name)) ?? v
      );
      const selectedSizeNorm = normalizeValue(selectedSize);
      const selectedColorNorm = normalizeValue(selectedColor);
      // Try to find size-based variation using exact matching
      const sizeVariation = variationImages.results.find((v) => {
        if (!selectedSizeNorm) return false;
        return getVarValue(v) === selectedSizeNorm;
      });

      if (sizeVariation) {
        const image = images.find((img) => img.listing_image_id === sizeVariation.image_id);
        if (image) imageUrl = image.url_fullxfull;
      }

      // If no size variation, try color-based variation
      if (!imageUrl) {
        const colorVariation = variationImages.results.find(v => {
          if (!selectedColorNorm) return false;
          return getVarValue(v) === selectedColorNorm;
        });

        if (colorVariation) {
          const imageId = parseInt(colorVariation.image_id, 10);
          const image = images.find(img => img.listing_image_id === imageId);
          if (image) {
            imageUrl = image.url_fullxfull;
          }
        }
      }
    }

    // Fallback: Try to map size to image based on order when no variation data exists
    if (!imageUrl && selectedSize && images.length > 1) {
      // Extract size name from selected size (e.g., "MICRO height 1.1 inches" -> "MICRO")
      const sizeMatch = selectedSize.match(/^(\w+)/);
      if (sizeMatch) {
        const sizeName = sizeMatch[1].toUpperCase();

        // Try to find the size in available sizes and map to corresponding image
        // This assumes sizes are ordered and images are ordered correspondingly
        const commonSizes = ['NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'XLARGE'];
        const sizeIndex = commonSizes.indexOf(sizeName);

        if (sizeIndex >= 0 && sizeIndex + 1 < images.length) {
          // Skip the first image (overall view) and map sizes to subsequent images
          imageUrl = images[sizeIndex + 1].url_fullxfull;
        }
      }
    }

    // Default to first image if nothing matched
    if (!imageUrl) {
      imageUrl = images[0]?.url_fullxfull || null;
    }

    // Return optimized URL for faster loading
    return optimizeImageUrl(imageUrl);
  }, [images, selectedSize, selectedColor, variationImages, optimizeImageUrl]);

  // Update current image when size or color changes
  useEffect(() => {
    const imageUrl = getCurrentImage();
    if (imageUrl && imageUrl !== currentImage) {
      setImageLoading(true);
      setCurrentImage(imageUrl);

      // Log priority loading
      if (isPriorityLoad) {
        logger.log('🎯 PRIORITY IMAGE LOAD:', imageUrl);
      }
    }
  }, [getCurrentImage, currentImage, selectedSize, selectedColor, isPriorityLoad]);

  // Get current price based on selected size and color using variation matrix
  const getCurrentPrice = useCallback(() => {
    if (!inventory?.products) return null;

    // Find an Etsy product whose property_values match the current selection exactly
    const matchesSelection = (product) => {
      const props = product.property_values || [];
      const sizeOrStyle = props.find(p => p.property_name === 'Size' || p.property_name === 'Style');
      const colorProp = props.find(p => p.property_name === 'Primary color');
      const sizeOk = selectedSize ? (sizeOrStyle?.values || []).some(v => String(v).toLowerCase() === String(selectedSize).toLowerCase()) : true;
      const colorOk = selectedColor ? (colorProp?.values || []).some(v => String(v).toLowerCase() === String(selectedColor).toLowerCase()) : true;
      return sizeOk && colorOk;
    };

    const product = inventory.products.find(matchesSelection) || inventory.products[0];
    const offering = product?.offerings?.[0];
    const price = offering?.price || product?.price;
    if (price && typeof price.amount === 'number' && price.divisor) {
      return price;
    }

    // If only a numeric priceValue was stored (rare), coerce
    if (typeof price === 'number') {
      return { amount: Math.round(price * 100), divisor: 100, currency_code: 'USD' };
    }
    return listingData?.price || null;
  }, [inventory, selectedSize, selectedColor, listingData]);

  // Update price when size or color changes
  useEffect(() => {
    const price = getCurrentPrice();
    // Only update state if the price actually changed
    const currentAmount = currentPrice?.amount ?? null;
    const currentDivisor = currentPrice?.divisor ?? null;
    const nextAmount = price?.amount ?? null;
    const nextDivisor = price?.divisor ?? null;

    const changed = currentAmount !== nextAmount || currentDivisor !== nextDivisor;
    if (changed) {
      setCurrentPrice(price || null);
      // Notify parent only when value changes and is valid
      if (onPriceUpdate && price && typeof price.amount === 'number' && typeof price.divisor === 'number' && price.divisor !== 0) {
        const priceValue = price.amount / price.divisor;
        onPriceUpdate(priceValue);
      }
    }
  }, [getCurrentPrice, onPriceUpdate, currentPrice]);

  // Notify parent of size/color changes
  useEffect(() => {
    if (onSizeColorChange) {
      onSizeColorChange(selectedSize, selectedColor);
    }
  }, [selectedSize, selectedColor, onSizeColorChange]);

  // Handle size change
  const handleSizeChange = (event) => {
    const newSize = event.target.value;
    setSelectedSize(newSize);
  };

  // Handle color change
  const handleColorChange = (event) => {
    const newColor = event.target.value;
    setSelectedColor(newColor);
  };

  // Handle add to scene
  const handleAddToScene = () => {
    // Open Etsy listing in new tab
    const listingEtsyUrl = listingData?.url || etsyUrl;
    if (listingEtsyUrl) {
      window.open(listingEtsyUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback to scene addition if no Etsy URL
      const stickerData = {
        listingId,
        title: listingData?.title || title,
        size: selectedSize,
        color: selectedColor,
        image: currentImage,
        etsyUrl: listingData?.url || etsyUrl,
        sizeVariations: listingData?.sizeVariations || [],
        colorVariations: listingData?.colorVariations || []
      };
      
      if (onAdd) {
        onAdd(stickerData);
      }
    }
  };

  // Handle card click
  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick();
    }
  };

  // If renderUI is provided, use it instead of default render
  if (renderUI) {
    const coreProps = {
      currentImage,
      imageLoading,
      listingData,
      selectedSize,
      selectedColor,
      availableSizes,
      availableColors,
      currentPrice,
      handleSizeChange,
      handleColorChange,
      handleAddToScene,
      handleCardClick,
      listingId,
      loading,
      error,
      dbData,
      images
    };
    return renderUI(coreProps);
  }

  // Default render logic
  // Loading state (or test mode)
  if (loading || testMode) {
    return (
      <div className="sticker-card loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <ClothedEyeLoader 
              size="large" 
              showTagline={false}
              showLogo={false}
              theme="light"
              message="Loading sticker details..."
            />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="sticker-card error">
        <div className="error-content">
          <MaterialIcon icon="error" size={24} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`sticker-card ${modalMode ? 'modal-mode' : ''}`} onClick={handleCardClick}>
      {/* Image Section */}
      <div className="card-image-container">
        {currentImage && (
          <img
            src={currentImage}
            alt={listingData?.title || title}
            className={`card-image ${imageLoading ? 'loading' : ''}`}
            loading={isPriorityLoad ? 'eager' : 'lazy'}
            fetchpriority={isPriorityLoad ? 'high' : 'low'}
            onLoad={() => {
              setImageLoading(false);
              if (onImageLoad) onImageLoad(); // Notify parent that image loaded
            }}
            onError={() => setImageLoading(false)}
          />
        )}
        {(imageLoading || forceImageLoading) && (
          <div className="image-loading-overlay">
            <ClothedEyeLoader
              size="small"
              showTagline={false}
              theme="light"
              className="image-loading-logo"
            />
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="card-content">
        {/* Title - Hidden in modal mode to avoid duplication */}
        {!modalMode && (
          <h3 className="card-title">
            {decodeHtmlEntities(listingData?.title || title)}
          </h3>
        )}

        {/* Category - Hidden in modal mode to save space */}
        {!modalMode && dbData?.category && (
          <div className="card-category">
            <MaterialIcon icon="category" size={14} />
            <span>{dbData.category}</span>
          </div>
        )}

        {/* Description (trim boilerplate in modal mode) */}
        {listingData?.description && (
          <p
            className="card-description"
            dangerouslySetInnerHTML={{
              __html: (() => {
                const raw = String(listingData.description || '');
                const decode = (s) => s
                  .replace(/&quot;/g, '"')
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&#39;/g, "'");
                const stripBoilerplate = (s) => {
                  let t = s;
                  // Preferred anchor: keep up to vinyl/adherence guidance, drop anything after
                  const anchorRegex = /made\s+from\s+high[-\s]?quality\s+fade[-\s]?resistant\s+vinyl[\s\S]*?direct\s+sunlight\s+if\s+possible\.?/i;
                  const anchorExec = anchorRegex.exec(t);
                  if (anchorExec) {
                    t = t.slice(0, anchorExec.index + anchorExec[0].length);
                  }
                  const patterns = [
                    /[^.!?]*shipping address[^.!?]*[.!?]/gi,
                    /[^.!?]*usps[^.!?]*[.!?]/gi,
                    /[^.!?]*tracking[^.!?]*[.!?]/gi,
                    /[^.!?]*(1-?2|one to two)\s+business\s+days[^.!?]*[.!?]/gi,
                    /[^.!?]*cannot be responsible[^.!?]*[.!?]/gi,
                    /[^.!?]*double-?check[^.!?]*[.!?]/gi,
                    /[^.!?]*lost[^.!?]*mail[^.!?]*[.!?]/gi,
                    /[^.!?]*allow up to\s*\d+\s*days[^.!?]*[.!?]/gi,
                    /[^.!?]*replacement[^.!?]*[.!?]/gi,
                    /\*+\s*note:[\s\S]*$/gi,
                    /[^.!?]*unconfirmed[^.!?]*address[^.!?]*[.!?]/gi,
                    /[^.!?]*your satisfaction is our priority[^.!?]*[.!?]/gi
                  ];
                  patterns.forEach((rx) => {
                    t = t.replace(rx, ' ');
                  });
                  return t
                    .replace(/\s{2,}/g, ' ')
                    .replace(/\n{2,}/g, '\n')
                    .trim();
                };
                const prepared = modalMode ? stripBoilerplate(decode(raw)) : decode(raw);
                // Convert newlines to <br> tags for proper display
                return prepared.replace(/\n/g, '<br>');
              })()
            }}
          />
        )}

        {/* Size and Color Selectors */}
        {(availableSizes.length > 0 || availableColors.length > 0) && (
          <div className="selectors-container">
            {/* Size Selector */}
            {availableSizes.length > 0 && (
              <div className="size-selector" style={{ display: 'flex', flexDirection: modalMode ? 'row' : 'column', alignItems: modalMode ? 'center' : 'flex-start', gap: modalMode ? 8 : 4, flex: 1 }}>
                <label htmlFor={`size-${listingId}`} style={{ margin: 0, whiteSpace: 'nowrap' }}>Size:</label>
                <select
                  id={`size-${listingId}`}
                  value={selectedSize}
                  onChange={handleSizeChange}
                  className="size-select"
                  style={{ flex: 1 }}
                >
                  {availableSizes.map(size => (
                    <option key={size} value={size}>{decodeHtmlEntities(size)}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Color Selector */}
            {availableColors.length > 0 && (
              <div className="color-selector" style={{ display: 'flex', flexDirection: modalMode ? 'row' : 'column', alignItems: modalMode ? 'center' : 'flex-start', gap: modalMode ? 8 : 4, flex: 1 }}>
                <label htmlFor={`color-${listingId}`} style={{ margin: 0, whiteSpace: 'nowrap' }}>Color:</label>
                <select
                  id={`color-${listingId}`}
                  value={selectedColor}
                  onChange={handleColorChange}
                  className="color-select"
                  style={{ flex: 1 }}
                >
                  {availableColors.map(color => (
                    <option key={color} value={color}>{decodeHtmlEntities(color)}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Disclaimer below selectors, centered and matching right column width */}
        {modalMode && null}


        
        {/* Admin-only Edit Listing button */}
        {isAdminMode && (
          <button 
            onClick={() => {
              // Navigate to listing admin with this listing ID
              window.location.href = `/listing-admin?listingId=${listingId}`;
            }}
            className="edit-listing-btn"
            disabled={loading}
            title="Edit this listing in admin panel"
          >
            <MaterialIcon icon="edit" size={16} />
            Edit Listing
          </button>
        )}
      </div>

      {/* Admin Mode Indicator */}
      {isAdminMode && (
        <div className="admin-indicator">
          <MaterialIcon icon="settings" size={16} />
          Admin Mode
        </div>
      )}
    </div>
  );
};

CoreStickerCard.propTypes = {
  listingId: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  etsyUrl: PropTypes.string,
  category: PropTypes.string,
  onAdd: PropTypes.func,
  globalSize: PropTypes.string,
  globalColor: PropTypes.string,
  isAdminMode: PropTypes.bool,
  onCardClick: PropTypes.func,
  buttonText: PropTypes.string,
  buttonIcon: PropTypes.string,
  renderUI: PropTypes.func,
  onPriceUpdate: PropTypes.func,
  onSizeColorChange: PropTypes.func
};

// Memoize to prevent unnecessary re-renders when parent updates
// Only re-render if listingId, globalSize, or globalColor changes
export default React.memo(CoreStickerCard, (prevProps, nextProps) => {
  return (
    prevProps.listingId === nextProps.listingId &&
    prevProps.globalSize === nextProps.globalSize &&
    prevProps.globalColor === nextProps.globalColor &&
    prevProps.isAdminMode === nextProps.isAdminMode &&
    prevProps.modalMode === nextProps.modalMode &&
    prevProps.isPriorityLoad === nextProps.isPriorityLoad
  );
}); 